import React, { useState, useEffect, useRef } from "react";
import DataPanel from "./components/DataPanel";
import useAxiosProtect from "../../../../hooks/useAxiosProtect";
import Graph from "./components/Graph";
import colors from "../../../../example_data/colors";
import './styling/containerStyle.css';
import Toolbar from "./components/Toolbar";
import CustomTimeframeModal from "./components/CustomTimeframeModal";
import EventsModal from "./components/EventsModal";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { electricUnitTypes, gasUnitTypes, waterUnitTypes, steamUnitTypes, temperatureUnitTypes } from "../../../../example_data/dropdowns/clientModal/loggingComponents";
import AirCard from "./air_compressors/AirCard";
import Button from '@mui/material/Button';

dayjs.extend(utc);
dayjs.extend(timezone);

const UtilityGraphContainer = props => {
    // top level vars
    const axiosProtect = useAxiosProtect();
    const siteID = props.siteID === undefined ? window.location.href.split('/')[window.location.href.split('/').length - 1] : props.siteID;
    var init = useRef(true);
    var specUpdateActive = useRef(false);

    const [plotSpecCooldown, setPlotSpecCooldown] = useState(true);

    // Graph vars
    const [data, setData] = useState([]);
    const [loggedUnits, setLoggedUnits] = useState([]);
    // var unitCategories = useRef({});
    const [maxYValue, setMaxYValue] = useState(1);
    const [errorMessage, setErrorMessage] = useState('');
    const [steamWarnings, setSteamWarnings] = useState([]);
    const [toolbarVisible, setToolbarVisible] = useState(false);
    const [panelVisible, setPanelVisible] = useState(false);
    var keepCheckedStates = useRef(false); // set to true if change made to plotSpecs, but not if utility type changed
    const defaultTimeframe = props.aircompressors ? '7d' : '4h';

    const initialHighlightState = {
        left: 'dataMin',
        right: 'dataMax',
        refAreaLeft: '',
        refAreaRight: '',
        bottom: 'auto',
        top: 'auto',
        bottom2: 'auto',
        top2: 'auto'
    };

    const [highlightState, setHighlightState] = useState(initialHighlightState);

    // Toolbar vars
    const [plotSpecs, setPlotSpecs] = useState({
        activeUtility: '',
        timeframe: '',
        metricType: '',
        metricOptions: [
            ''
        ],
        resolution: props.aircompressors ? '1h': '',
        customStart: '',
        customEnd: '',
        aggregateMethod: 'average',
        dataFetchMethod: 'adjusted'
    });

    const [selectedDates, setSelectedDates] = useState({
        start: null, 
        end: null, 
        startTzString: '',
        endTzString: '',
        tzName: 'America/New_York', 
        currentTimeInTz: dayjs(),
        refreshReady: false,
        editable: false
    });

    var toolLoad = useRef(false);
    const [scopeData, setScopeData] = useState({
        hasWater: false,
        hasElec: false,
        hasGas: false
    });

    //data panel
    const [accordionKey, setAccordionKey] = useState('0');

    const [customTimeframeModalOpen, setCustomTimeframeModalOpen] = useState(false);
    const [eventsModalOpen, setEventsModalOpen] = useState(false);
    const [eventInfoModalOpen, setEventInfoModalOpen] = useState(false);

    const [eventIndex, setEventIndex] = useState(null);
    const [events, setEvents] = useState([]);
    const [selectedEvents, setSelectedEvents] = useState([]);

    const [insights, setInsights] = useState(null);
    const [airMetrics, setAirMetrics] = useState(null);

    const generateMetricOptions = utility => {
        if(utility === 'water'){
            return [
                'L/min',
                'L',
                'm³',
                'm³/h',
                'impgal',
                'impgal/min',
                'USG',
                'USG/min'
            ];
        }else if(utility === 'electricity'){
            return [
                'Current (A)',
                'kW',
                'kWh'
            ];
        }else if(utility === 'gas'){
            return [
                'm³/h',
                'm³'
            ];
        }else if(utility === 'steam'){
            return ['lbs/h', 'kg/h', 'BTU/h', 'kJ/h'];
        }else if(utility === 'temperature'){
            return [
                '°C',
                '°F'
            ];
        }
    }

    const getCurrentTimeInTimezone = tz => {
        const fmt = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false, timeZoneName: 'shortOffset', // e.g. "GMT-04:00"
        });

        const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
        const m = /GMT([+-])(\d{2}):(\d{2})/.exec(parts.timeZoneName);
        if (!m) return Date.now(); // fallback

        const sign = m[1] === '+' ? 1 : -1;
        const offMin = sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));

        // Build the wall-clock in that TZ, then convert to the correct UTC instant
        const wallUtcMs = Date.UTC(
            Number(parts.year), Number(parts.month) - 1, Number(parts.day),
            Number(parts.hour), Number(parts.minute), Number(parts.second)
        );
        return wallUtcMs - offMin * 60_000;
    }

    const convertTSStampToISO = timestamp => {
        return timestamp.replace(" ", "T").split(".")[0] + "Z";
    }

    const getTimezoneInfo = (timestamp, ianaString) => {
        try{
            const date = new Date(convertTSStampToISO(timestamp))

            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: ianaString,
                timeZoneName: 'short',
            });

            const parts = formatter.formatToParts(date);
            const tzPart = parts.find(part => part.type === 'timeZoneName');

            return tzPart ? tzPart.value : '';
        }catch(error){
            return '';
        }
    }

    useEffect(() => {
        if(!plotSpecCooldown) return;

        const tm = setTimeout(() => setPlotSpecCooldown(false), 1500);
        return () => clearTimeout(tm);
    }, [plotSpecCooldown]);

    useEffect(() => {
        if(init.current){
            init.current = false;

            axiosProtect.get(`/s/sites/get-site-fields?fields=%5B%22has_water%22,%22has_elec%22,%22has_gas%22,%22tz_name%22%5D&siteID=${siteID}`).then(response => {
                if(response.status !== 200) return;

                try{
                    setScopeData(prev => ({
                        ...prev,
                        ['hasWater']: response.data.siteData.has_water,
                        ['hasElec']: response.data.siteData.has_elec,
                        ['hasGas']: response.data.siteData.has_gas
                    }));

                    setSelectedDates(prev => ({
                        ...prev,
                        ['tzName']: response.data.siteData.tz_name,
                        ['currentTimeInTz']: dayjs(getCurrentTimeInTimezone(response.data.siteData.tz_name))
                    }));

                    let utilityType;
                    if(['water', 'electricity', 'gas', 'steam'].includes(window.name)){
                        utilityType = window.name;

                        setPlotSpecs(prev => ({
                            ...prev,
                            [`activeUtility`]: utilityType,
                            ['timeframe']: '4h',
                            ['metricType']: utilityType === 'water' ? 'L/min' : utilityType === 'electricity' ? 'Current (A)' : utilityType === 'steam' ? 'lbs/h' : 'm³/h',
                            ['metricOptions']: generateMetricOptions(utilityType),
                            ['resolution']: '1m'
                        }));
                    }
                    else if(props.aircompressors && response.data.siteData.has_elec){
                        utilityType = 'electricity';

                        setPlotSpecs(prev => ({
                            ...prev,
                            [`activeUtility`]: utilityType,
                            ['timeframe']: '7d',
                            ['metricType']: 'kW',
                            ['metricOptions']: generateMetricOptions(utilityType),
                            ['resolution']: '1h',
                            ['aggregateMethod']: 'average'
                        }));

                        populateGraphData('electricity');

                    }else if(response.data.siteData.has_water){
                        utilityType = 'water';

                        setPlotSpecs(prev => ({
                            ...prev,
                            [`activeUtility`]: utilityType,
                            ['timeframe']: '4h',
                            ['metricType']: 'L/min',
                            ['metricOptions']: generateMetricOptions(utilityType),
                            ['resolution']: '1m'
                        }));
                    }
                    else if(response.data.siteData.has_elec){
                        utilityType = 'electricity';

                        setPlotSpecs(prev => ({
                            ...prev,
                            [`activeUtility`]: utilityType,
                            ['timeframe']: '4h',
                            ['metricType']: 'Current (A)',
                            ['metricOptions']: generateMetricOptions(utilityType),
                            ['resolution']: '1m',
                            ['aggregateMethod']: 'average'
                        }));

                        populateGraphData('electricity');

                    }else if(response.data.siteData.has_gas){
                        utilityType = 'gas';

                        setPlotSpecs(prev => ({
                            ...prev,
                            [`activeUtility`]: utilityType,
                            ['timeframe']: '4h',
                            ['metricType']:  'm³/h',
                            ['metricOptions']: generateMetricOptions(utilityType),
                            ['resolution']: '1m'
                        }));

                        populateGraphData('gas');
                    }

                    window.name = '';

                    populateGraphData(utilityType);

                }catch(err){
                    window.name = '';
                }
            }).catch(err => {console.log(err)});
        }
    }, []);

    useEffect(() => {
        setCustomTimeframeModalOpen(false);
        setEventInfoModalOpen(false);
        if(!specUpdateActive.current) return;

        updateYAxisMax(loggedUnits);
        setInsights(null);

        if(toolLoad.current){
            toolLoad.current = false;
            populateGraphData();
        }

    }, [plotSpecs]);

    const populateGraphData = async (utilityType=null) => {
        let resolution = plotSpecs.resolution === '' ? '1m' : plotSpecs.resolution;
        let timeframe = plotSpecs.timeframe === '' ? (props.aircompressors ? '7d' : '4h') : plotSpecs.timeframe;
        const requestedUtility = utilityType || plotSpecs.activeUtility;
        setSteamWarnings([]);
        const requestedMetric = plotSpecs.metricType || generateMetricOptions(requestedUtility)?.[0] || '';
        let waterUnits = requestedUtility !== 'water' ? 'none' : requestedMetric === 'm³/h' ? 'm3h' : requestedMetric === 'm³' ? 'm3' : requestedMetric;

        if(!plotSpecCooldown) setPlotSpecCooldown(true);
        
        const aircompressors = props.aircompressors ? true : false;
        const rawDataRoute = 'raw-data-v2';
        const selectedMetric = encodeURIComponent(requestedMetric);
        let payloadString = plotSpecs.timeframe === 'custom' ?
        `r/${rawDataRoute}/dataset?id=${siteID}&resolution=${resolution}&start=${selectedDates.start.startOf('minute').unix()}&end=${selectedDates.end.startOf('minute').unix()}&utility=${requestedUtility}&isCustom=true&method=${plotSpecs.aggregateMethod}&waterUnits=${waterUnits}&tempUnits=${requestedMetric === '°C' ? 'C' : requestedMetric === '°F' ? 'F' : 'null'}&metric=${selectedMetric}&aircompressors=${aircompressors}&fetchMethod=${plotSpecs.dataFetchMethod}` :
        `r/${rawDataRoute}/dataset?id=${siteID}&resolution=${resolution}&start=${timeframe}&utility=${requestedUtility}&method=${plotSpecs.aggregateMethod}&waterUnits=${waterUnits}&tempUnits=${requestedMetric === '°C' ? 'C' : requestedMetric === '°F' ? 'F' : 'null'}&metric=${selectedMetric}&aircompressors=${aircompressors}&fetchMethod=${plotSpecs.dataFetchMethod}`;

        try{
            let response = await axiosProtect.get(payloadString);
        
            if(response.status === 204){
                setData([]);
                setLoggedUnits([]);
                setErrorMessage(requestedUtility === 'steam' ? 'No steam meters registered for this site.' : "Data for requested utility is not in this site's scope.");
                specUpdateActive.current = true;
                return;

            }else if(response.status !== 200){
                setErrorMessage("Something went wrong. Please try again later or contact support.");
                specUpdateActive.current = true;
                return;

            }else if(response.data.data == null || response.data.data.length === 0){
                setErrorMessage('No data available for selected time range');
                specUpdateActive.current = true;
                return;
            }else setErrorMessage('');

            setSelectedDates(prev => ({
                ...prev,
                ['start']: response.data.startTime == null ? null : dayjs(convertTSStampToISO(response.data.startTime)),
                ['startTzName']: response.data.startTime == null ? '' : getTimezoneInfo(response.data.startTime, selectedDates.tzName),
                ['end']: response.data.endTime == null ? null : dayjs(convertTSStampToISO(response.data.endTime)),
                ['endTzName']: response.data.endTime == null ? '' : getTimezoneInfo(response.data.endTime, selectedDates.tzName),
                ['refreshReady']: false
            }));

            if(!keepCheckedStates.current){
                for(let i = 0;i<response.data.devices.length;++i){
                    if(props.aircompressors){
                        response.data.devices[i].checked = response.data.devices[i].unit_type === 'aircomp';
                    }else{
                        response.data.devices[i].checked = response.data.devices[i].isCategory === true;
                    }
                    response.data.devices[i].opened = false;
                }
            }else{
                let renderedUnits = {};
                for(let i = 0;i<loggedUnits.length;++i){
                    renderedUnits[loggedUnits[i].id] = loggedUnits[i].checked; 
                }

                for(let i = 0;i<response.data.devices.length;++i){
                    response.data.devices[i].checked = renderedUnits[response.data.devices[i].id];
                }
            }

            setSteamWarnings(response.data.warnings || []);
            setLoggedUnits(response.data.devices);

            addPowerMetric(response.data.data);

            for(let i = 0;i<response.data.data.length;++i){
                response.data.data[0].events = {value: 0, ids: []}
            }

            setData(response.data.data);
            setEvents(response.data.events);

            if (props.aircompressors && response.data.vfdkWh !== undefined) {
                setAirMetrics({
                    vfdkWh: response.data.vfdkWh,
                    loadedkW: response.data.loadedkW,
                    unloadedkW: response.data.unloadedkW,
                    leakPercent: response.data.leakPercent,
                    leakscfm: response.data.leakscfm,
                    leakskW: response.data.leakskW
                });
            }

            updateYAxisMax(response.data.devices, response.data.data);

            specUpdateActive.current = true;

        }catch(err){
            setErrorMessage(err.response?.data?.error || "Something went wrong. Please try again later or contact support.");
        }

        keepCheckedStates.current = false;
    }

    const updateYAxisMax = (devices, dataArray=null) => {
        let activeDevices = {};

        for(let i = 0;i<devices.length;++i){
            if(devices[i].checked){
                activeDevices[devices[i].name] = 0;
            }
        }

        let maxValue = -1;
        if(data == null && dataArray == null){
            setMaxYValue(1);
            return;
        }

        let activeKey = plotSpecs.metricType === 'kWh' ? 'power_kwh' : (plotSpecs.metricType === 'kW' ? 'kw' : 'current');

        if(dataArray == null){
            for(let i = 0;i<data.length;++i){
                for(let key in data[i]){
                    if(activeDevices.hasOwnProperty(key)) maxValue = Math.max(maxValue, parseFloat(data[i][key][activeKey]))
                }
            }
        }else{
            for(let i = 0;i<dataArray.length;++i){
                for(let key in dataArray[i]){
                    if(activeDevices.hasOwnProperty(key)) maxValue = Math.max(maxValue, parseFloat(dataArray[i][key][activeKey]))
                }
            }
        }

        if (plotSpecs.metricType === 'kWh') {
            if (maxValue === -1 || !Number.isFinite(maxValue)) {
                setMaxYValue(1);
                return;
            }

            const padded = maxValue * 1.05;
            const ceiling = padded <= 1 ? Math.ceil(padded * 10) / 10 : Math.ceil(padded);
            setMaxYValue(ceiling);
            return;
        }

        maxValue = maxValue === -1 ? 1 : Math.max(1, parseInt(1.05 * maxValue) + 1);
        maxValue = Math.ceil(maxValue / 5) * 5;
        
        setMaxYValue(maxValue);
    }

    const toggleCheckedUnit = (id, enableAll=null) => {
        let temp = [...loggedUnits];
        let lastChangedValue = null;

        if(enableAll != null){
            //use to enable or disable all units, not toggle specific unit, else leave param 2 null
            for(let i = 0;i<temp.length;++i){
                temp[i].checked = enableAll;
            }

            setLoggedUnits(temp);
            lastChangedValue = enableAll;

        }else{
            let temp = [...loggedUnits];

            for(let i = 0;i<temp.length;++i){
                if(temp[i].id === id){
                    temp[i].checked = !temp[i].checked;
                    lastChangedValue = temp[i].checked;
                    setLoggedUnits(temp);
                    break;
                }
            }
        }

        updateYAxisMax(temp);
        return lastChangedValue;
    }

    const toggleAllUnitsInCategory = (categoryName, valueToSet) => {
        let units = [...loggedUnits];

        for(let i = 0;i<units.length;++i){
            if(units[i].isCategory === true || units[i].unit_type !== categoryName) continue;

            units[i].checked = valueToSet;
        }

        setLoggedUnits([...units]);
        updateYAxisMax(units);
    }

    const toggleOpenCategory = id => {
        let temp = [...loggedUnits];

        for(let i = 0;i<temp.length;++i){
            if(temp[i].id === id){
                temp[i].opened = !temp[i].opened;
                break;
            }
        }

        setLoggedUnits([...temp]);
    }

    const getMinCustomResolution = () => {
        const daysBetween = Math.abs(selectedDates.end.diff(selectedDates.start, 'day', true));

        if(daysBetween <= 3) {
            return '1m';
        }else if(daysBetween <= 7) {
            return '15m';
        }else if(daysBetween <= 30) {
            return '1h';
        }else{
            return '1d';
        }
    }

    const computeMinimumResolution = timeframe => {
        if(timeframe === '1h') return '1m';
        else if(timeframe === '4h') return '1m';
        else if(timeframe === '1d') return '5m';
        else if(timeframe === '7d') return '1h';
        else if(timeframe === '14d') return '1h';
        else if(timeframe === '31d') return '1h';
        else if(timeframe === '365d') return '1d';
        else if(timeframe === 'custom') return getMinCustomResolution();
    }

    const processAggregateMethod = metric => {
        if([
            'L/min',
            'Current (A)',
            'kW',
            'm³/h',
            'impgal/min',
            'USG/min',
            '°C',
            '°F', 'lbs/h', 'kg/h', 'BTU/h', 'kJ/h'
        ].includes(metric)) return 'average';

        return 'sum';
    }

    const updatePlotSpecs = (e, field, eventModalTrigger=false) => {
        setSelectedDates(prev => ({
            ...prev,
            ['refreshReady']: false
        }));
        
        if(field !== 'activeUtility'){
            keepCheckedStates.current = true;

            setData([]);
            setErrorMessage('')
            toolLoad.current = true;

            if(field === 'timeframe'){
                setPlotSpecs(prev => ({
                    ...prev,
                    [`${field}`]: e.target.value,
                    resolution: eventModalTrigger ? '15m' : computeMinimumResolution(e.target.value)
                }));
            }else if(field === 'metricOptions'){
                setPlotSpecs(prev => ({
                    ...prev,
                    ['metricType']: e,
                    ['aggregateMethod']: processAggregateMethod(e)
                }));

            }else if(field === 'metricType' && e.target.value === 'Current (A)'){
                setPlotSpecs(prev => ({
                    ...prev,
                    [`${field}`]: e.target.value,
                    ['aggregateMethod']: 'average'
                }));
            }else{
                setPlotSpecs(prev => ({
                    ...prev,
                    [`${field}`]: e.target.value
                }));
            }
        }else{
            let options = generateMetricOptions(e.target.value);

            // if(e.target.value === 'water'){
            //     options = ['Water usage (L)'];
            //     // options = ['Flow rate (LPM)'];
            // }else if(e.target.value === 'electricity'){
            //     options = ['Current (A)','Power (kW)']
            // }else if(e.target.value === 'gas'){
            //     options = ['Gas usage (m3)']
            // }

            setData([]);
            setErrorMessage('');
            toolLoad.current = true;

            setPlotSpecs(prev => ({
                ...prev,
                [`${field}`]: e.target.value,
                ['metricType']: options[0],
                ['metricOptions']: options,
                ['aggregateMethod']: ['electricity', 'steam', 'temperature'].includes(e.target.value) ? 'average' : plotSpecs.aggregateMethod
            }));
        }
    };

    const resToHours = (res) => {
        if (!res) return 0;
        if (res === '1m')  return 1/60;
        if (res === '5m')  return 1/60;
        if (res === '15m') return 1/60;
        if (res === '1h')  return 1/60;
        if (res === '1d')  return 1/60;
        const m = /^(\d+)\s*m$/.exec(res);
        return m ? Number(m[1]) / 60 : 0;
    }

    const addPowerMetric = readings => {
        const intervalHours = resToHours(plotSpecs.resolution || '1m') || (1/60);

        for (let i = 0; i < readings.length; ++i) {
            const row = readings[i];
            for (const key in row) {
                if (key === 'time' || key === 'events') continue;

                const v = row[key];
                if (!v || typeof v !== 'object') continue;

                if (plotSpecs.activeUtility !== 'electricity') continue;

                // Ensure kW exists: prefer provided v.kw; else derive from I * PF * V * sqrt3 / 1000
                let kw = Number(v.kw);
                if (!Number.isFinite(kw)) {
                    const amps = Number(v.current);
                    const pf   = Number(v.power_factor);
                    const volt = Number(v.voltage);
                    const derived = amps * pf * volt * Math.sqrt(3) / 1000;
                    if (Number.isFinite(derived)) kw = derived;
                }
                if (Number.isFinite(kw)) v.kw = kw;

                const existingKwh = Number(v.power_kwh);
                if (!Number.isFinite(existingKwh)) {
                    const kwh = Number.isFinite(kw) ? kw * intervalHours : NaN;
                    if (Number.isFinite(kwh)) v.power_kwh = kwh;
                }
            }
        }
    }

    const openModal = () => {
        setCustomTimeframeModalOpen(true);
    }

    return(
        <>
        {customTimeframeModalOpen && <CustomTimeframeModal customTimeframeModalOpen={customTimeframeModalOpen} 
            setCustomTimeframeModalOpen={setCustomTimeframeModalOpen} setPlotSpecs={setPlotSpecs} toolLoad={toolLoad}
            setData={setData} setErrorMessage={setErrorMessage}
            />
        }

        {eventsModalOpen && <EventsModal eventsModalOpen={eventsModalOpen} setEventsModalOpen={setEventsModalOpen} events={events} setEvents={setEvents}/>}

        <div className="chart-container">
            <div className="toolbar-toggle-row">
                <span style={{ 
                    fontSize: '0.95em', 
                    fontWeight: '600', 
                    color: 'rgb(78,79,83)',
                    textTransform: 'capitalize'
                }}>
                    {plotSpecs.activeUtility || ''}
                </span>

                <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                    <Button 
                        variant="outlined" 
                        color="info"
                        size="small"
                        onClick={() => {
                            setToolbarVisible(prev => {
                                if(!prev) setPanelVisible(false);
                                return !prev;
                            });
                        }}
                    >
                        {toolbarVisible ? 'Hide Filters' : 'Filters'}
                    </Button>

                    <Button 
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => {
                            setPanelVisible(prev => {
                                if(!prev) setToolbarVisible(false);
                                return !prev;
                            });
                        }}
                    >
                        {panelVisible ? 'Hide Sensors' : 'Sensors'}
                    </Button>
                </div>
            </div>

            <div className={`chart-comp-panel${panelVisible ? '' : ' toolbar-hidden'}`} style={{display: props.aircompressors ? 'block'  : 'none', overflowY:'scroll'}}>
                <AirCard
                    loggedUnits={loggedUnits} toggleCheckedUnit={toggleCheckedUnit} plotSpecs={plotSpecs}
                    isClient={props.isClient} sites={props.sites} resetAuthCheck={props.resetAuthCheck}
                    accordionKey={accordionKey} setAccordionKey={setAccordionKey} events={events}
                    setEvents={setEvents} selectedEvents={selectedEvents} setPlotSpecs={setPlotSpecs}
                    toolLoad={toolLoad} setData={setData} setErrorMessage={setErrorMessage}
                    eventInfoModalOpen={eventInfoModalOpen} setEventInfoModalOpen={setEventInfoModalOpen}
                    specUpdateActive={specUpdateActive} insights={insights} toggleOpenCategory={toggleOpenCategory}
                    data={data} toggleAllUnitsInCategory={toggleAllUnitsInCategory} scopeData={scopeData}
                    updatePlotSpecs={updatePlotSpecs} errorMessage={errorMessage} plotSpecCooldown={plotSpecCooldown}
                    setSelectedDates={setSelectedDates} aircompressors={props.aircompressors}
                    airMetrics={airMetrics}
                />       
            </div>
            <div className="chart-comp-panel" style={{display: props.aircompressors ? 'none'  : 'block'}}>
                <div className="chart-comp-body" >
                    <DataPanel 
                        loggedUnits={loggedUnits} toggleCheckedUnit={toggleCheckedUnit} plotSpecs={plotSpecs}
                        isClient={props.isClient} sites={props.sites} resetAuthCheck={props.resetAuthCheck} 
                        accordionKey={accordionKey} setAccordionKey={setAccordionKey} events={events}
                        setEvents={setEvents} selectedEvents={selectedEvents} setPlotSpecs={setPlotSpecs}
                        toolLoad={toolLoad} setData={setData} setErrorMessage={setErrorMessage}
                        eventInfoModalOpen={eventInfoModalOpen} setEventInfoModalOpen={setEventInfoModalOpen}
                        specUpdateActive={specUpdateActive} insights={insights} toggleOpenCategory={toggleOpenCategory}
                        data={data} toggleAllUnitsInCategory={toggleAllUnitsInCategory} scopeData={scopeData} 
                        updatePlotSpecs={updatePlotSpecs} errorMessage={errorMessage} plotSpecCooldown={plotSpecCooldown} 
                        setSelectedDates={setSelectedDates} aircompressors={props.aircompressors} 
                    />
                </div>
            </div>

            <div className='chart-divider'></div>

            <div className="chart-graph-container">
                

                

                <div className={`chart-graph-controls${toolbarVisible ? '' : ' toolbar-hidden'}`}>
                    <Toolbar 
                        siteID={siteID} plotSpecs={plotSpecs} updatePlotSpecs={updatePlotSpecs} scopeData={scopeData} data={data} openModal={openModal} 
                        setEventsModalOpen={setEventsModalOpen} errorMessage={errorMessage} selectedDates={selectedDates} setSelectedDates={setSelectedDates}
                        setPlotSpecs={setPlotSpecs} plotSpecCooldown={plotSpecCooldown} loggedUnits={loggedUnits} aircompressors={props.aircompressors}
                        useV2RawData={!props.aircompressors}/>
                </div>

                <div className='chart-graph-body'>
                    <div className="chart-graph-graph">
                        <div className="mobile-y-label">{plotSpecs.metricType}</div>
                        <Graph 
                            data={data} loggedUnits={loggedUnits} maxYValue={maxYValue} plotSpecs={plotSpecs} 
                            errorMessage={errorMessage} eventIndex={eventIndex} setEventIndex={setEventIndex} 
                            accordionKey={accordionKey} setAccordionKey={setAccordionKey} setSelectedEvents={setSelectedEvents}
                            highlightState={highlightState} setHighlightState={setHighlightState} setInsights={setInsights} 
                            selectedDates={selectedDates} aircompressors={props.aircompressors}
                        />
                    </div>

                    <div className="chart-graph-legend">
                        <ul>
                            {loggedUnits.map((unit, i) => {
                                if(unit.checked){
                                    let unitName = unit.name;

                                    if(unit.isCategory === true){
                                        unitName = (
                                            plotSpecs.activeUtility === 'gas' ? gasUnitTypes : 
                                            plotSpecs.activeUtility === 'water' ? waterUnitTypes : 
                                            plotSpecs.activeUtility === 'electricity' ? electricUnitTypes :
                                            plotSpecs.activeUtility === 'steam' ? steamUnitTypes : temperatureUnitTypes
                                        ).find(u => u.value === unitName.split('-')[1])?.text ?? unitName.split('-')[1];
                                    }

                                    return <li className='chart-legend-element' style={{color: `${colors[i % colors.length]}`}}><span>{unitName}</span></li>;
                                }
                            })}
                        </ul>
                    </div>
                </div> 
            </div>
        </div>
        </>
    );
}

export default UtilityGraphContainer;
